import { Link, useLocation } from 'wouter';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function Navbar({ solid = false }: { solid?: boolean } = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const homeHref = import.meta.env.BASE_URL;
  const [scrolled, setScrolled] = useState(false);
  // Rest suppresses lingering hover/focus after the latch is cleared.
  const [logoState, setLogoState] = useState<'ready' | 'latched' | 'rest'>('ready');
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        menuButton.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? `bg-white backdrop-blur-none shadow-sm ${scrolled ? 'border-b border-border py-4' : 'border-b border-transparent py-6'}` : scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-border py-4 shadow-sm' : 'bg-white/50 backdrop-blur-md border-b border-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link
          href="/"
          className="plasmics-logo-link flex items-center gap-3 group"
          aria-label="Plasmics home"
          data-logo-state={logoState}
          onPointerEnter={() => setLogoState(state => state === 'rest' ? 'ready' : state)}
          onFocus={() => setLogoState(state => state === 'rest' ? 'ready' : state)}
          onClick={() => setLogoState(state => state === 'latched' ? 'rest' : 'latched')}
          onPointerLeave={() => setLogoState('rest')}
          onBlur={() => setLogoState('rest')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="-4.14 -2.48 91.68 79.45"
            width="91.68"
            height="79.45"
            aria-hidden="true"
            focusable="false"
            className="w-8 h-auto shrink-0 overflow-visible"
          >
            <g fill="#1D1D1B">
              <g className="plasmics-logo-line plasmics-logo-line--top">
                <polygon points="10.950 0.000 20.367 16.310 28.045 16.310 22.468 6.650 79.561 6.650 83.400 0.000" />
              </g>
              <g className="plasmics-logo-line plasmics-logo-line--left">
                <polygon points="36.225 62.744 45.642 46.434 41.802 39.784 36.225 49.444 7.679 0.000 -0.000 0.000" />
              </g>
              <g className="plasmics-logo-line plasmics-logo-line--right">
                <polygon points="77.925 9.483 59.092 9.483 55.252 16.133 66.407 16.133 37.861 65.577 41.700 72.227" />
              </g>
            </g>
          </svg>
          <span className="font-sans font-bold tracking-[0.15em] text-sm hidden sm:block mt-0.5 text-foreground">
            PLASMICS
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-8">
          <a href={`${homeHref}#technology`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase">Technology</a>
          <a href={`${homeHref}#applications`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase">Applications</a>
          <a href={`${homeHref}#invest`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase">Invest</a>
          <Link href="/team" aria-current={location === '/team' ? 'page' : undefined} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase">Team & Careers</Link>
          <Link href="/blog" aria-current={location === '/blog' ? 'page' : undefined} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase" data-testid="link-blog">Founder's Blog</Link>
          
          <a href={`${homeHref}#contact`} className="ml-4 px-6 py-2.5 bg-foreground text-background font-mono text-xs font-bold tracking-widest uppercase hover:bg-primary hover:text-primary-foreground transition-colors">
            Contact
          </a>
        </nav>

        {/* Mobile Nav Toggle */}
        <button 
          ref={menuButton}
          type="button"
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
          className="lg:hidden text-foreground p-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav id="mobile-navigation" aria-label="Mobile navigation" className="absolute top-full left-0 right-0 bg-white border-b border-border p-6 flex flex-col gap-6 lg:hidden shadow-lg">
          <a href={`${homeHref}#technology`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase" onClick={() => setIsOpen(false)}>Technology</a>
          <a href={`${homeHref}#applications`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase" onClick={() => setIsOpen(false)}>Applications</a>
          <a href={`${homeHref}#invest`} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase" onClick={() => setIsOpen(false)}>Invest</a>
          <Link href="/team" onClick={() => setIsOpen(false)} aria-current={location === '/team' ? 'page' : undefined} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase">Team & Careers</Link>
          <Link href="/blog" onClick={() => setIsOpen(false)} aria-current={location === '/blog' ? 'page' : undefined} className="text-sm font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors uppercase" data-testid="link-blog-mobile">Founder's Blog</Link>
          <a href={`${homeHref}#contact`} onClick={() => setIsOpen(false)} className="mt-4 inline-block w-full text-center px-6 py-4 bg-foreground text-background font-mono text-xs font-bold tracking-widest uppercase hover:bg-primary transition-colors">
            Contact
          </a>
        </nav>
      )}
    </header>
  );
}
